/**
 * OPS-11: Incident Mode Banner
 *
 * Full-width alert banner shown when the Control Tower detects an
 * operational incident. Triggers when:
 *   - pulse.status === 'critical'
 *   - OR criticalAlerts >= 2
 *   - OR guestRisks >= 3
 *
 * Pure frontend detection — no backend changes required.
 */

import type {
  PulseScoreCore,
  OperationalAlert,
  GuestExperienceRisk,
} from '../../services/controlTowerTypes';
import { lowestDimension } from '../../../utils/pressureUtils';

// ─── Incident Detection ──────────────────────────────────────────────────────

export interface IncidentInfo {
  active: boolean;
  headline: string;
  detail: string;
  startedAt: number | null;
}

export function detectIncident(
  pulse: PulseScoreCore,
  alerts: OperationalAlert[],
  risks: GuestExperienceRisk[],
): IncidentInfo {
  const criticalAlerts = alerts.filter((a) => a.severity === 'critical');
  const isCriticalPulse = pulse.status === 'critical';
  const isMultiCritical = criticalAlerts.length >= 2;
  const isHighRisk = risks.length >= 3;

  if (!isCriticalPulse && !isMultiCritical && !isHighRisk) {
    return { active: false, headline: '', detail: '', startedAt: null };
  }

  // Build contextual headline from the primary trigger
  let headline: string;
  let detail: string;

  if (isCriticalPulse) {
    headline = 'Operational Incident Detected';
    const weakest = lowestDimension(pulse.dimensions);
    detail = `Pulse critical (${pulse.score}/100) — ${weakest.name} dimension at ${weakest.score}`;
  } else if (isMultiCritical) {
    headline = 'Multiple Critical Alerts Active';
    detail = `${criticalAlerts.length} critical alerts — ${criticalAlerts[0]!.title}${criticalAlerts.length > 1 ? ` and ${criticalAlerts.length - 1} more` : ''}`;
  } else {
    headline = 'Guest Experience Under Pressure';
    detail = `${risks.length} guest risks detected across operations`;
  }

  // Use earliest critical alert as incident start, or fallback to now
  const startedAt = criticalAlerts.length > 0
    ? Math.min(...criticalAlerts.map((a) => a.detectedAt))
    : Date.now();

  return { active: true, headline, detail, startedAt };
}

// ─── Component ───────────────────────────────────────────────────────────────

interface IncidentBannerProps {
  incident: IncidentInfo;
}

export function IncidentBanner({ incident }: IncidentBannerProps) {
  if (!incident.active) return null;

  const startedLabel = incident.startedAt
    ? new Date(incident.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 px-5 py-3 shadow-lg shadow-rose-500/5 animate-pulse-slow" role="alert" aria-live="assertive">
      <div className="flex items-center gap-3">
        {/* Pulsing warning icon */}
        <div className="flex-shrink-0">
          <span className="relative flex h-5 w-5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-40" />
            <span className="relative inline-flex items-center justify-center h-5 w-5 rounded-full bg-rose-500/30">
              <svg className="w-3.5 h-3.5 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </span>
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-rose-400 uppercase tracking-wide">
              {incident.headline}
            </span>
          </div>
          <p className="text-xs text-rose-300/80 mt-0.5 truncate">{incident.detail}</p>
        </div>

        {/* Started timestamp */}
        {startedLabel && (
          <div className="flex-shrink-0 text-right">
            <span className="text-[10px] text-rose-400/60 uppercase">Started</span>
            <p className="text-xs font-mono text-rose-300 tabular-nums">{startedLabel}</p>
          </div>
        )}
      </div>
    </div>
  );
}
